// ============================================================
// SecureAccessHub — /square/command-center/olivium/secure-access.
//
// V2 operator hub over the REAL Secure Access spine (sq_access_codes). Lists
// issued codes with their true status (active/claimed/revoked/expired), summary
// tiles, and the universal target-type registry (the 5 PHASE_2L types labelled
// pending-migration). Admin-gated (minLevel 80) — honest auth/forbidden states.
// Codes are hash-at-rest; the plaintext is only ever shown once at issue.
// ============================================================

import CommandCenterShell from '../../shell/CommandCenterShell'
import { CC_INK, CC_MONO } from '../../shell'
import { OLIVIUM_ACCENT } from '../portal-factory/portalFactory'
import { SectionCard, StateBlock, chipBtn, primaryBtn } from '../portal-factory/PortalKit'
import { SECURE_TARGET_TYPES, ACCESS_STATUS_META } from './secureAccessData'
import type { CommandCenterAccessCode } from '../../../../api/client'

const HONESTY = {
  label: 'SECURE ACCESS · NOT FAKED',
  title: 'Codes are real, hash-at-rest (sq_access_codes) and admin-gated. The one-time plaintext is shown exactly once at issue and never again. A code bound to an unrouted target honestly reports no destination. GitHub repo permission is never granted here (GITHUB_PERMISSION_DEFERRED).',
}

function StatusPill({ status }: { status: string }) {
  const m = ACCESS_STATUS_META[status] || { label: status.toUpperCase(), accent: CC_INK.muted }
  return <span style={{ fontSize: 9, fontWeight: 800, letterSpacing: '0.1em', fontFamily: CC_MONO, color: m.accent, padding: '3px 8px', borderRadius: 5, background: m.accent + '18', border: `1px solid ${m.accent}33` }}>{m.label}</span>
}

export interface SecureAccessHubProps {
  codes: CommandCenterAccessCode[]
  summary: { active: number; claimed: number; revoked: number; expired: number; total: number } | null
  loading: boolean
  error: string | null
  authRequired: boolean
  forbidden: boolean
  onHome?: () => void
  onBackCommandCenter?: () => void
  /** Optional "back to the Olivium governance suite" control (suite integration). */
  onBackToSuite?: () => void
  onCreate?: () => void
  onOpenCode?: (id: string) => void
}

export default function SecureAccessHub(props: SecureAccessHubProps) {
  const { codes, summary, loading, error, authRequired, forbidden, onHome, onBackCommandCenter, onBackToSuite, onCreate, onOpenCode } = props

  return (
    <CommandCenterShell
      onHome={onHome} onBackCommandCenter={onBackCommandCenter}
      parentBack={onBackToSuite ? { label: '← Olivium', onClick: onBackToSuite } : undefined}
      breadcrumb={['OLIVIUM', 'SECURE ACCESS']}
      title="Olivium Secure Access" titleAccent={OLIVIUM_ACCENT} honestyBadge={HONESTY}
      contextBar={
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, width: '100%' }}>
          <span style={{ fontSize: 11, color: CC_INK.muted, flex: 1 }}>Issue + manage controlled access codes bound to a target.</span>
          <button type="button" style={primaryBtn} onClick={onCreate}>+ Issue secure access</button>
        </div>
      }
    >
      <div>
        {loading && <StateBlock kind="loading" title="Loading access codes…" />}
        {!loading && authRequired && <StateBlock kind="auth" title="Sign in to load Secure Access" />}
        {!loading && forbidden && <StateBlock kind="error" title="Operator access required" body="Secure Access is admin-gated (minLevel 80)." />}
        {!loading && !authRequired && !forbidden && error && <StateBlock kind="error" title="Could not load access codes" body={error} />}

        {!loading && !authRequired && !forbidden && !error && (
          <div>
            {summary && (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(90px, 1fr))', gap: 8, marginBottom: 14 }}>
                {[['Active', summary.active, '#059669'], ['Claimed', summary.claimed, '#0891b2'], ['Revoked', summary.revoked, '#dc2626'], ['Expired', summary.expired, '#6a7480'], ['Total', summary.total, OLIVIUM_ACCENT]].map(([label, val, accent]) => (
                  <div key={label as string} style={{ padding: '10px 12px', borderRadius: 10, background: (accent as string) + '12', border: `1px solid ${accent}2a` }}>
                    <div style={{ fontSize: 20, fontWeight: 900, color: accent as string }}>{val as number}</div>
                    <div style={{ fontSize: 9, fontWeight: 800, letterSpacing: '0.12em', color: CC_INK.muted, fontFamily: CC_MONO }}>{(label as string).toUpperCase()}</div>
                  </div>
                ))}
              </div>
            )}

            <SectionCard title="Issued codes" right={<span style={{ fontSize: 10, color: CC_INK.faint, fontFamily: CC_MONO }}>{codes.length}</span>}>
              {codes.length === 0 ? (
                <StateBlock kind="empty" title="No access codes yet" body="Issue one above. Nothing is seeded." />
              ) : (
                <div>
                  {codes.map((c) => (
                    <button key={c.id} type="button" onClick={() => onOpenCode?.(c.id)} style={{ display: 'flex', alignItems: 'center', gap: 12, width: '100%', textAlign: 'left', padding: '9px 0', borderBottom: '1px solid rgba(255,255,255,0.05)', background: 'transparent', border: 'none', borderBottomStyle: 'solid', cursor: 'pointer' }}>
                      <span style={{ fontSize: 12.5, color: CC_INK.primary, flex: 1 }}>{c.label}</span>
                      <span style={{ fontSize: 10, color: CC_INK.faint, fontFamily: CC_MONO }}>{c.code_prefix}</span>
                      <span style={{ fontSize: 10, color: CC_INK.muted, fontFamily: CC_MONO }}>{c.target_label || (c.target_type ? c.target_type.replace(/_/g, ' ') : 'unbound')}</span>
                      <span style={{ fontSize: 9, color: CC_INK.faint, fontFamily: CC_MONO }}>{c.use_count}/{c.max_uses}</span>
                      <StatusPill status={c.status} />
                    </button>
                  ))}
                </div>
              )}
            </SectionCard>

            <SectionCard title="Target types" hint="The universal Olivium Secure Access targets. All 12 types are live (migration 20260710000001 applied 2026-07-10).">
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: 8 }}>
                {SECURE_TARGET_TYPES.map((t) => (
                  <div key={t.value} style={{ padding: 10, borderRadius: 9, background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 3 }}>
                      <span style={{ fontSize: 12, fontWeight: 700, color: CC_INK.primary, flex: 1 }}>{t.label}</span>
                      {t.phase2l && <span style={{ fontSize: 8, fontWeight: 800, fontFamily: CC_MONO, color: '#3d9a6c', padding: '2px 6px', borderRadius: 4, background: 'rgba(52,211,153,0.08)', border: '1px solid rgba(52,211,153,0.22)' }}>NEW</span>}
                    </div>
                    {t.note && <p style={{ margin: 0, fontSize: 10, color: CC_INK.faint, lineHeight: 1.4 }}>{t.note}</p>}
                  </div>
                ))}
              </div>
            </SectionCard>

            <div style={{ fontSize: 10, color: CC_INK.faint, fontFamily: CC_MONO }}>
              Advanced operations (claim, redeem-to-destination) remain on the existing Secure Access surface at /square/olivium/secure-access.
            </div>
          </div>
        )}
      </div>
    </CommandCenterShell>
  )
}
